import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CommonService } from 'src/app/services/common-service/common.service';

type UnsubscribePageState =
  | 'form'
  | 'unsubscribed'
  | 'subscribed'
  | 'alreadyUnsubscribed'
  | 'invalid'
  | 'employee';

@Component({
  selector: 'app-unsubscribe',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './unsubscribe.component.html',
  styleUrls: ['./unsubscribe.component.scss']
})
export class UnsubscribeComponent implements OnInit {
  unsubscribeForm!: FormGroup;
  token = '';
  pageState: UnsubscribePageState = 'form';
  loading = false;
  submitted = false;

  reasons = [
    'I receive too many emails',
    'The content is not relevant to me',
    'I no longer need these emails',
    'I never signed up for these emails',
    'Other'
  ];

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private commonService: CommonService
  ) {}

  get selectedReason(): string {
    return this.unsubscribeForm?.get('reason')?.value || '';
  }

  ngOnInit(): void {
    this.initializeForm();
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    if (!this.isValidGuid(this.token)) {
      this.pageState = 'invalid';
      return;
    }

    this.loading = true;
    this.commonService.getSubscriptionStatus(this.token).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.pageState = response?.isSubscribed === false ? 'alreadyUnsubscribed' : 'form';
      },
      error: (error: any) => {
        this.loading = false;
        this.pageState = error.status === 403 ? 'employee' : 'invalid';
      }
    });
  }

  unsubscribe(): void {
    this.submitted = true;
    if (this.unsubscribeForm.invalid || this.loading) {
      return;
    }

    let reason = this.selectedReason;
    if (reason === 'Other') {
      reason = this.unsubscribeForm.get('otherReason')?.value?.trim() || 'Other';
    }

    this.loading = true;
    this.commonService.unsubscribeContact(this.token, reason).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response?.statusCode === 200) {
          this.pageState = 'unsubscribed';
        }
      },
      error: (error: any) => {
        this.loading = false;
        if (error.status === 409) {
          this.pageState = 'alreadyUnsubscribed';
        } else if (error.status === 403) {
          this.pageState = 'employee';
        } else if (error.status === 404) {
          this.pageState = 'invalid';
        } else {
          console.error('Unsubscribe request failed:', error);
        }
      }
    });
  }

  subscribeAgain(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.commonService.subscribeContact(this.token).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response?.statusCode === 200) {
          this.pageState = 'subscribed';
        }
      },
      error: (error: any) => {
        this.loading = false;
        if (error.status === 409) {
          this.pageState = 'subscribed';
        } else if (error.status === 403) {
          this.pageState = 'employee';
        } else if (error.status === 404) {
          this.pageState = 'invalid';
        } else {
          console.error('Subscribe request failed:', error);
        }
      }
    });
  }

  selectReason(reason: string): void {
    const control = this.unsubscribeForm.get('reason');
    control?.setValue(reason);
    control?.markAsTouched();
    control?.markAsDirty();
    control?.updateValueAndValidity();
  }

  private initializeForm(): void {
    this.unsubscribeForm = this.formBuilder.group({
      reason: ['', Validators.required],
      otherReason: ['']
    });
  }

  private isValidGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }
}